import { useState, useSyncExternalStore } from "react";
import { acknowledgeLaunchWalletActivity, launchPersistenceFailed, recheckLaunchTx, subscribeLaunchTx } from "../../../wallet/launchpad-transactions";
import { useLaunchTransaction } from "../state/use-launch-wallet";

const labels = {
  wallet: "Waiting for wallet approval…", pending: "Broadcast · waiting for receipt…",
  uncertain: "Transaction outcome unknown. Check wallet activity.", rejected: "Wallet request canceled.",
  reverted: "Transaction reverted.", confirmed: "Transaction confirmed.",
  cancelled: "Transaction cancelled by a verified replacement.", replaced: "A different transaction used this nonce.",
  "event-error": "Receipt confirmed, but the expected TokenCreated event is missing or does not match.",
};
export function LaunchTransactionStatus({ scope, allowRecovery = true }: { scope: string; allowRecovery?: boolean }) {
  return <TransactionStatus key={scope} scope={scope} allowRecovery={allowRecovery} />;
}

function TransactionStatus({ scope, allowRecovery }: { scope: string; allowRecovery: boolean }) {
  const row = useLaunchTransaction(scope);
  const [checking, setChecking] = useState(false);
  const [error, setError] = useState("");
  const [acknowledge, setAcknowledge] = useState(false);
  const [replacement, setReplacement] = useState("");
  const [original, setOriginal] = useState("");
  const [signedOriginal, setSignedOriginal] = useState("");
  const storageFailed = useSyncExternalStore(subscribeLaunchTx, launchPersistenceFailed);
  const warning = storageFailed && <p role="alert">Recovery storage is unavailable. Keep this tab open and check your wallet activity.</p>;
  if (!row) return warning || null;
  function checkReceipt(hash?: string, originalHash?: string) {
    setChecking(true); setError("");
    const proof = hash && (row?.hash || originalHash) ? signedOriginal.trim() : undefined;
    setSignedOriginal("");
    const result = originalHash ? recheckLaunchTx(scope, hash, proof, originalHash) : recheckLaunchTx(scope, hash, proof);
    void result.catch((cause: unknown) =>
      setError(cause instanceof Error ? cause.message : "Could not verify the transaction. Check wallet activity."))
      .finally(() => setChecking(false));
  }
  return <div>
    <p role="status">{labels[row.state]} {row.hash && <small style={{ overflowWrap: "anywhere" }}>{row.hash}</small>}</p>
    {warning}
    {row.originalHash && <p>Original hash <small style={{ overflowWrap: "anywhere" }}>{row.originalHash}</small></p>}
    {allowRecovery && (row.state === "uncertain" || row.state === "event-error") && row.hash && <>
      <button type="button" disabled={checking} onClick={() => checkReceipt()}>Check receipt</button>
      {row.state === "uncertain" && <details>
        <summary>Recover a cancelled or replaced transaction</summary>
        <p>If you sped up or cancelled the transaction, copy the mined replacement hash from wallet activity.</p>
        <label>Replacement transaction hash <input value={replacement} onChange={event => setReplacement(event.target.value)} /></label>
        {row.nonce === undefined && <label>Signed original transaction (if the RPC cannot find it)
          <input type="password" autoComplete="off" value={signedOriginal} onChange={event => setSignedOriginal(event.target.value)} />
          <small>Use the original signed transaction from your wallet or RPC. It is verified locally.</small>
        </label>}
        <button type="button" disabled={checking || !/^0x[\da-f]{64}$/i.test(replacement.trim())}
          onClick={() => checkReceipt(replacement.trim())}>Verify replacement</button>
      </details>}
    </>}
    {allowRecovery && row.state === "uncertain" && !row.hash && <>
      <p>Check wallet activity. Enter the hash once the transaction is mined, or confirm there is no pending request or broadcast.</p>
      <label>Original transaction hash <input value={original} onChange={event => setOriginal(event.target.value)} /></label>
      <button type="button" disabled={checking || !/^0x[\da-f]{64}$/i.test(original.trim())}
        onClick={() => checkReceipt(original.trim())}>Recover transaction</button>
      <details>
        <summary>Recover a cancelled or replaced transaction</summary>
        <label>Signed original transaction
          <input type="password" autoComplete="off" value={signedOriginal} onChange={event => setSignedOriginal(event.target.value)} />
        </label>
        <p>Provide the original hash above and the signed original transaction from your wallet or RPC. The signed bytes are verified locally.</p>
        <label>Replacement transaction hash <input value={replacement} onChange={event => setReplacement(event.target.value)} /></label>
        <button type="button" disabled={checking || !signedOriginal.trim() ||
          ![original, replacement].every(hash => /^0x[\da-f]{64}$/i.test(hash.trim()))}
          onClick={() => checkReceipt(replacement.trim(), original.trim())}>Verify replacement</button>
      </details>
      {!original.trim() && !replacement.trim() && !signedOriginal.trim() && (acknowledge ? <button type="button" disabled={checking} onClick={() => {
        setError("");
        try { acknowledgeLaunchWalletActivity(scope); setAcknowledge(false); }
        catch (cause) { setError(cause instanceof Error ? cause.message : "Could not save recovery status."); }
      }}>I verified nothing was broadcast</button> :
        <button type="button" disabled={checking} onClick={() => setAcknowledge(true)}>Resolve using wallet activity</button>)}
    </>}
    {error && <p role="alert">{error}</p>}
  </div>;
}
